import React, { useState } from 'react';

const MementoModal = ({ memento, onClose, onUpdate, onDelete }) => {
  const [visibility, setVisibility] = useState(memento.visibility);

  const handleSave = () => {
    const updatedMemento = {
      ...memento,
      visibility: visibility
    };
    onUpdate(updatedMemento);
  };

  const handleDelete = () => {
    if (window.confirm('Are you sure you want to delete this memento?')) {
      onDelete(memento.id);
    }
  };

  const formatTimestamp = (timestamp) => {
    return new Date(timestamp).toLocaleString();
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50 p-5" onClick={onClose} data-testid="modal-overlay">
      <div className="bg-white rounded-xl w-full max-w-2xl max-h-[80vh] overflow-y-auto shadow-2xl animate-in slide-in-from-bottom-4 fade-in duration-300" onClick={(e) => e.stopPropagation()} data-testid="modal-content">
        <div className="flex justify-between items-center p-5 border-b border-gray-200">
          <h2 className="m-0 text-gray-800 text-xl font-semibold">{memento.label}</h2>
          <button className="bg-transparent border-none text-2xl cursor-pointer text-gray-600 p-0 w-8 h-8 flex items-center justify-center rounded-full hover:bg-gray-100 transition-colors duration-200" onClick={onClose}>×</button>
        </div>
        
        <div className="p-6">
          <div className="mb-6">
            <div className="py-3">
              <strong className="block mb-4">Select visibility:</strong>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <div 
                  className={`p-4 rounded-lg border-2 cursor-pointer transition-colors duration-200 ${
                    visibility === 'public' 
                      ? 'border-brand-500 bg-brand-50' 
                      : 'border-gray-200 bg-white hover:border-gray-300'
                  }`}
                  onClick={() => setVisibility('public')}
                >
                  <div className="flex items-center justify-between mb-2">
                    <h3 className="font-semibold text-gray-900">Public</h3>
                    {visibility === 'public' && (
                      <div className="w-5 h-5 bg-brand-500 rounded-full flex items-center justify-center">
                        <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                        </svg>
                      </div>
                    )}
                  </div>
                  <p className="text-sm text-gray-600">Visible to everyone and can be shared</p>
                </div>

                <div 
                  className={`p-4 rounded-lg border-2 cursor-pointer transition-colors duration-200 ${
                    visibility === 'private' 
                      ? 'border-brand-500 bg-brand-50' 
                      : 'border-gray-200 bg-white hover:border-gray-300'
                  }`}
                  onClick={() => setVisibility('private')}
                >
                  <div className="flex items-center justify-between mb-2">
                    <h3 className="font-semibold text-gray-900">Private</h3>
                    {visibility === 'private' && (
                      <div className="w-5 h-5 bg-brand-500 rounded-full flex items-center justify-center">
                        <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                        </svg>
                      </div>
                    )}
                  </div>
                  <p className="text-sm text-gray-600">Only visible to you, completely private</p>
                </div>

                <div 
                  className={`p-4 rounded-lg border-2 cursor-pointer transition-colors duration-200 ${
                    visibility === 'ask me' 
                      ? 'border-brand-500 bg-brand-50' 
                      : 'border-gray-200 bg-white hover:border-gray-300'
                  }`}
                  onClick={() => setVisibility('ask me')}
                >
                  <div className="flex items-center justify-between mb-2">
                    <h3 className="font-semibold text-gray-900">Ask Me</h3>
                    {visibility === 'ask me' && (
                      <div className="w-5 h-5 bg-brand-500 rounded-full flex items-center justify-center">
                        <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                        </svg>
                      </div>
                    )}
                  </div>
                  <p className="text-sm text-gray-600">Ask for permission before sharing</p>
                </div>
              </div>
            </div>
          </div>
          
          <div className="mt-6">
            <strong className="block mb-3">Content:</strong>
            <div className="min-h-24 p-4 bg-white border border-gray-300 rounded-md leading-relaxed text-gray-800 text-sm focus-within:border-brand-500 focus-within:ring-1 focus-within:ring-brand-500">
              {memento.content}
            </div>
            <div className="mt-2 text-xs text-gray-500">
              {formatTimestamp(memento.timestamp)}
            </div>
          </div>
        </div>
        
        <div className="flex justify-between items-center p-5 border-t border-gray-200">
          <button 
            className="text-red-600 hover:text-red-800 text-sm font-medium cursor-pointer bg-transparent border-none underline transition-colors duration-200" 
            onClick={handleDelete}
          >
            Delete
          </button>
          <button 
            className="px-5 py-2.5 border-none rounded-md text-sm font-medium cursor-pointer transition-colors duration-200 bg-brand-500 text-white hover:bg-brand-600" 
            onClick={handleSave}
          >
            Save
          </button>
        </div>
      </div>
    </div>
  );
};

export default MementoModal;
